import { useCallback, useEffect, useState } from 'react';
import { fetchBookingsList } from '../api/bookingApi';
import { filterBookingsForAsset } from '../utils/assetOwnerBookingCalendar';
import { normalizeBookingListRecords } from '../utils/bookingListNormalize';

export type AssetOwnerBookingsState = {
  bookings: Record<string, unknown>[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

export function useAssetOwnerBookings(
  assetId: string | null,
  enabled: boolean,
): AssetOwnerBookingsState {
  const [bookings, setBookings] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchNonce, setFetchNonce] = useState(0);

  const refetch = useCallback(() => {
    setFetchNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    const trimmedId = assetId?.trim() ?? '';
    if (!enabled || !trimmedId) {
      setBookings([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const data = await fetchBookingsList();
        if (cancelled) {
          return;
        }
        const records = normalizeBookingListRecords(data);
        setBookings(filterBookingsForAsset(records, trimmedId));
      } catch (cause) {
        if (cancelled) {
          return;
        }
        setBookings([]);
        setError(cause instanceof Error ? cause.message : 'Failed to load bookings.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assetId, enabled, fetchNonce]);

  return { bookings, loading, error, refetch };
}
